import React, { useMemo, useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Modal, Select, DatePicker, Button, Spin } from "antd";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import Table from "../../../core/pagination/datatable";
import GRAN_LOGO1 from "../Logo/Granules_logo1.png";
import { getApiBaseUrl, ADD_ACTIVITY, LOGIN } from "../Config/Config";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;

const BarcodeReport = () => {
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false); // Loader for reauthentication
  const [passwordError, setPasswordError] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState(0);
  const [dateRange, setDateRange] = useState([
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), //7 days ago
    new Date(), // Current date and time
  ]);
  const [filters, setFilters] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  const user = JSON.parse(sessionStorage.getItem("user")) || {
    UserName: "Unknown User",
    UserID: null,
  };

  // Static report configuration
  const reportConfig = useMemo(() => {
    const now = new Date();
    const pad = (n) => (n < 10 ? "0" + n : n);
    const day = pad(now.getDate());
    const month = pad(now.getMonth() + 1);
    const year = now.getFullYear();
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());
    return {
      ID: "barcode",
      title: "Barcode Report",
      reportType: "Barcode",
      filename: `barcode_report_${day}-${month}-${year}_${hours}-${minutes}-${seconds}`,
      columns: [
        {
          title: "Date & Time",
          key: "createdon",
          render: (date) => {
            if (!date) return "";
            const d = new Date(date.replace("T", " "));
            const day = String(d.getUTCDate()).padStart(2, "0");
            const month = String(d.getUTCMonth() + 1).padStart(2, "0");
            const year = d.getUTCFullYear();
            const hours = String(d.getUTCHours()).padStart(2, "0");
            const minutes = String(d.getUTCMinutes()).padStart(2, "0");
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          },
          // render: (text) => dayjs(text).format("DD/MM/YYYY"),
          // render: (text) => dayjs(text).format("HH:mm:ss"),
        },
        { title: "User ID", key: "username" },
        { title: "Filter ID", key: "Lable" },
        { title: "Remarks", key: "Remarks" },
      ],
      showEquipmentId: false,
    };
  }, []);

  // Fetch barcode report data
  const fetchBarcodeData = async () => {
    setLoading(true);
    try {
      const pad = (n) => (n < 10 ? "0" + n : n);

      const start = dateRange[0];
      const end = dateRange[1];

      const formatDateTime = (date) => {
        return (
          date.getFullYear() +
          "-" +
          pad(date.getMonth() + 1) +
          "-" +
          pad(date.getDate()) +
          " " +
          pad(date.getHours()) +
          ":" +
          pad(date.getMinutes()) +
          ":" +
          pad(date.getSeconds())
        );
      };
      const startDate = formatDateTime(start);
      const endDate = formatDateTime(end);
      const response = await fetch(
        `${getApiBaseUrl()}/api/getbarcodereport?FilterId=${selectedFilterId}&startdate=${encodeURIComponent(
          startDate
        )}&enddate=${encodeURIComponent(endDate)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (data.Status && Array.isArray(data.ResultData)) {
        setDataSource(data.ResultData);
      } else {
        setDataSource([]);
      }
    } catch (error) {
      console.error("Error fetching barcode data:", error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/getFiltersListDropdown`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        const result = data?.ResultData || [];
        setFilters(result);
      })
      .catch(() => setFilters([]));
  }, []);

  // Fetch barcode data when filters or date range change
  useEffect(() => {
    fetchBarcodeData();
  }, [selectedFilterId, dateRange]);

  const tableColumns = useMemo(
    () =>
      reportConfig.columns.map((col) => ({
        title: col.title,
        dataIndex: col.key,
        key: col.key,
        render: col.render || ((text) => text || "NA"),
        sorter: (a, b) => {
          const valA = a[col.key] ?? "";
          const valB = b[col.key] ?? "";
          return typeof valA === "number"
            ? valA - valB
            : String(valA).localeCompare(String(valB));
        },
      })),
    [reportConfig]
  );

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr.replace("T", " "));
    const pad = (n) => (n < 10 ? "0" + n : n);
    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const logoWidth = 15;
    const logoHeight = 15;
    const pageWidth = doc.internal.pageSize.getWidth();

    const generateHeader = () => {
      doc.addImage(GRAN_LOGO1, "PNG", 10, 10, logoWidth, logoHeight);
      doc.setFontSize(11);
      doc.text("Granules India Limited", 30, 14);
      doc.text("Gagillapur", 30, 20);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(`Title: ${reportConfig.title}`, 10, 35);
      doc.setFont(undefined, "normal");
      return 42;
    };

    const startY = generateHeader();

    autoTable(doc, {
      startY: startY,
      head: [reportConfig.columns.map((col) => col.title)],
      body:
        dataSource.length > 0
          ? dataSource.map((item) =>
              reportConfig.columns.map((col) =>
                col.key === "createdon" && col.title === "Date & Time"
                  ? formatDate(item[col.key])
                  : col.render
                  ? col.render(item[col.key])
                  : item[col.key] ?? ""
              )
            )
          : [
              [
                {
                  content: "No Data Available",
                  colSpan: reportConfig.columns.length,
                  styles: { halign: "center" },
                },
              ],
            ],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
      margin: { top: startY, bottom: 35 },
      didDrawPage: () => {
        generateHeader();
      },
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - 40,
        doc.internal.pageSize.height - 10
      );
      if (i === totalPages) {
        const pageHeight = doc.internal.pageSize.height;
        const footerY = pageHeight - 20;
        doc.text("Remarks:", 10, footerY - 10);
        doc.text(`Printed by: ${user.UserName}`, 10, footerY);
        const pad = (n) => (n < 10 ? "0" + n : n);
        const start = dateRange[0];
        const end = dateRange[1];
        const startFormatted = `${pad(start.getDate())}-${pad(
          start.getMonth() + 1
        )}-${start.getFullYear()} ${pad(start.getHours())}:${pad(
          start.getMinutes()
        )}`;
        const endFormatted = `${pad(end.getDate())}-${pad(
          end.getMonth() + 1
        )}-${end.getFullYear()} ${pad(end.getHours())}:${pad(
          end.getMinutes()
        )}`;
        doc.text(
          `Date Range: ${startFormatted} - ${endFormatted}`,
          10,
          footerY + 6
        );
        const now = new Date();
        const day = pad(now.getDate());
        const month = pad(now.getMonth() + 1);
        const year = now.getFullYear();
        const hours = pad(now.getHours());
        const minutes = pad(now.getMinutes());
        const seconds = pad(now.getSeconds());
        doc.text(
          `Printed on: ${day}-${month}-${year} ${hours}:${minutes}:${seconds}`,
          10,
          footerY + 12
        );
      }
    }

    const pdfBlob = doc.output("blob");

    const formData = new FormData();
    formData.append("file", pdfBlob, `${reportConfig.filename}.pdf`);

    fetch(`${getApiBaseUrl()}/api/uploadReportCopy`, {
      method: "POST",
      body: formData,
      headers: {},
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Upload success:", data);
      })
      .catch((err) => {
        console.error("Upload failed:", err);
      });

    doc.save(`${reportConfig.filename}.pdf`);
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }

    setAuthLoading(true);
    setPasswordError("");
    const username = user?.UserName || user?.Username;
    let loginSuccessful = false;

    try {
      const loginRes = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const loginData = await loginRes.json();
      if (loginData?.Message === "Login successful") {
        loginSuccessful = true;
      } else {
        setPasswordError(
          "Reauthentication failed. Please check your password."
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      setPasswordError("Something went wrong. Please try again.");
    }

    try {
      const activityPayload = {
        ActivityType: 1,
        PerformedBy: user?.UserID,
        PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
        Notes: loginSuccessful
          ? `${reportConfig.title} downloaded by ${username}`
          : `Failed Reauthentication for ${reportConfig.title} by ${username}`,
        Location: "Reports",
        IsSucces: loginSuccessful ? 1 : 0,
      };

      const actRes = await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activityPayload),
      });

      const actData = await actRes.json();
      if (!actData?.success) {
        console.warn("Activity log failed");
      }
    } catch (err) {
      console.error("Activity log error:", err);
    } finally {
      setAuthLoading(false);
      if (loginSuccessful) {
        setVisible(false);
        setPassword("");
        handleExportPDF();
      }
    }
  };

  const handleFilterIdChange = (value) => {
    setSelectedFilterId(value);
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0].toDate(), dates[1].toDate()]);
    } else {
      setDateRange([
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        new Date(),
      ]);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/assetmgt/dashboard">
                      <i className="fas fa-home fa-lg" />
                    </Link>
                  </li>
                  <h4 className="breadcrumb-item" aria-current="page">
                    {reportConfig.title}
                  </h4>
                </ol>
              </nav>
              <h6>Manage your barcode reports</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderTooltip}>
                <Link
                  to="#"
                  onClick={() => {
                    setVisible(true);
                    setPassword("");
                    setPasswordError("");
                  }}
                >
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>
        <div className="card table-list-card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="search-set"></div>
            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap gap-3">
              <div style={{ width: 320 }}>
                <Select
                  style={{ width: "100%" }}
                  value={selectedFilterId === 0 ? 0 : selectedFilterId}
                  onChange={handleFilterIdChange}
                  placeholder="Select Filter ID"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option key={0} value={0}>
                    All
                  </Option>
                  {filters?.map((filter) => (
                    <Option key={filter?.ID} value={filter?.ID}>
                      {filter?.Lable}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <RangePicker
                  showTime={{ format: "HH:mm" }}
                  value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
                  onChange={handleDateRangeChange}
                  format="DD-MM-YYYY HH:mm"
                  style={{ width: 350 }}
                  defaultValue={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
                />
                <Button
                  type="primary"
                  onClick={fetchBarcodeData}
                  loading={loading}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <div className="text-center my-4">
                  <Spin size="large" />
                  <p>Loading data...</p>
                </div>
              ) : dataSource?.length > 0 ? (
                <Table columns={tableColumns} dataSource={dataSource} />
              ) : (
                <p className="text-center my-4">No data available</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        title="Confirm Password"
        open={visible}
        onCancel={() => {
          setVisible(false);
          setPassword("");
          setPasswordError("");
        }}
        onOk={handlePasswordSubmit}
        confirmLoading={authLoading}
        okText="Verify & Download"
        width={350}
      >
        <div className="mb-3">
          <div className="pass-group">
            <input
              type={showPassword ? "text" : "password"}
              className={`form-control ${passwordError ? "is-invalid" : ""}`}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError("");
              }}
              autoComplete="new-password"
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />
            <span
              className={`ti toggle-password ${
                showPassword ? "ti-eye" : "ti-eye-off"
              }`}
              onClick={() => setShowPassword((prev) => !prev)}
            ></span>
          </div>
          {passwordError && (
            <div className="invalid-feedback" style={{ display: "block" }}>
              {passwordError}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default BarcodeReport;
